<template>
  <div v-if="surveillance">
    <h2>Observation des pratiques
      <span class="label" ref="title" :class="`label-${module}`">
        {{ moduleName }}
      </span>
    </h2>
    <hr>
    <div v-if="year === '2027'">
      <div class="row" >
        <div class="col-lg-9">
          <alert type="warning">
            <p>
              <strong>
                Le module <span class="label label-reaExplaur">RÉA expl AUR</span>
                est en cours d'adaptation au protocole {{ year }}.
              </strong>
            </p>
          </alert>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="row" >
        <div class="col-lg-10">
          <div v-if="status === 0">
            <alert type="warning">
              <h4>
                Bienvenue sur l'espace
                <span class="label label-reaExplaur">RÉA expl AUR</span>
                du programme SPIADI {{year}}
              </h4>
              <p>
                <strong>
                  Merci de remplir le formulaire ci-dessous afin que nous
                  puissions envoyer vos écouvillons au plus vite.
                </strong>
              </p>
              <p>
                Ce formulaire contient :
                <ul>
                  <li>Le nom et l'adresse du destinataire des écouvillons</li>
                  <li>
                    Pour chaque service inscrit, la Fiche service correspondante
                  </li>
                </ul>
              </p>
            </alert>
            <h3>Destinataire des écouvillons</h3>
            <hr>
            <sui-field-list v-model="model" :fields="fields" :waiting="waiting">
            </sui-field-list>
            <div v-for="u in units" :key="`${u.unit.name}-${u.unit.ufType}`">
              <h3>Fiche service : {{u.unit.name}} ({{u.code}})</h3>
              <hr>
              <sui-field-list v-model="u.model" :fields="unitFields"
                              :waiting="waiting">
              </sui-field-list>
            </div>
            <hr>
            <alert type="warning" v-if="!validForm">
              Merci de bien remplir tous les champs du formulaire ci-dessus avant
              de pouvoir l'envoyer.
            </alert>
            <collapse v-model="formError.error">
              <p>
                <sui-error-panel :error="formError" v-if="formError.error">
                </sui-error-panel>
              </p>
            </collapse>
            <div class="end-button">
              <a role="button" @click="send" class="btn btn-reaExplaur"
                 :disabled="waiting || !validForm">
                Envoyer le formulaire
              </a>
            </div>
          </div>
          <div v-else-if="status === 1">
            <alert type="info">
              <h4>
                Bienvenue sur l'espace
                <span class="label label-reaExplaur">RÉA expl AUR</span>
                du programme SPIADI {{year}}
              </h4>
              <p>
                Nous avons bien reçu le formulaire de destination des écouvillons
                ainsi que les fiches concernant le.s service.s suivant :
              </p>
              <p>
                <ul>
                  <li v-for="u in units" :key="`${u.unit.name}-${u.unit.ufType}-li`">
                    <span class="label label-reaExplaur">
                      {{u.unit.name}} ({{u.code}})
                    </span>
                  </li>
                </ul>
              </p>
              <p>
                <strong>
                  Les écouvillons sont en cours de préparation par notre équipe et
                  seront envoyés au plus vite à l'adresse suivante :
                </strong>
              </p>
              <div class="panel panel-default panel-address">
                <div class="panel-body text-center">
                  <strong>{{moduleData.dest.establishment}}</strong><br>
                  <strong>
                    {{moduleData.dest.firstName}} {{moduleData.dest.name}}
                  </strong><br>
                  <strong>{{moduleData.dest.role}}</strong><br>
                  <strong>{{moduleData.dest.service}}</strong><br>
                  <strong>{{moduleData.dest.address}}</strong><br>
                </div>
              </div>
              <p>
                <strong>
                  Si cette adresse vous semble erronée, merci de nous contacter au
                  plus vite via le <router-link to="/contact">formulaire de contact
                  </router-link>.
                </strong>
              </p>
            </alert>
            <alert type="warning">
              <p>
                Une fois les écouvillons réceptionnés, vous pourrez débuter
                l'enquête : pour chaque bactériémie à <i>S. aureus</i>, revenez
                sur cette page pour générer le numéro unique de l'écouvillon
                et remplir la Fiche Patient associée.
              </p>
            </alert>
          </div>
          <div v-else-if="status === 2">
            <alert type="warning">
              <h4>
                Bienvenue sur l'espace
                <span class="label label-reaExplaur">RÉA expl AUR</span>
                du programme SPIADI {{year}}
              </h4>
              <p>
                Les écouvillons ont été envoyés à l'adresse suivante :
              </p>
              <div class="panel panel-default panel-address">
                <div class="panel-body text-center">
                  <strong>{{moduleData.dest.establishment}}</strong><br>
                  <strong>
                    {{moduleData.dest.firstName}} {{moduleData.dest.name}}
                  </strong><br>
                  <strong>{{moduleData.dest.role}}</strong><br>
                  <strong>{{moduleData.dest.service}}</strong><br>
                  <strong>{{moduleData.dest.address}}</strong><br>
                </div>
              </div>
              <p class="text-center">
                <btn type="success" @click="notifyReceived" :disabled="waiting">
                  J'ai bien reçu les écouvillons
                </btn>
              </p>
            </alert>
          </div>
          <div v-else>
            <modal v-model="patientModal">
              <template slot="title">
                Fiche Patient
                <span class="label label-reaExplaur">
                  RÉA expl AUR
                </span>
              </template>
              <template slot="footer">
                <alert type="warning" v-if="!patientValidForm" class="text-justify">
                  Merci de bien remplir tous les champs du formulaire ci-dessus avant
                  de pouvoir l'envoyer.
                </alert>
                <div>
                  <a role="button" @click="sendPatient" class="btn btn-reaExplaur"
                     :disabled="waiting || !patientValidForm">
                    Envoyer le formulaire
                  </a>
                </div>
              </template>
              <div v-if="target.unit">
                <div class="form-group">
                  <label class="control-label">
                    NUMÉRO DE FICHE
                  </label>
                  <input disabled
                         class="form-control"
                         :value="`${target.unit.label}${target.swab + 1}`"
                         type="text"/>
                </div>
                <div class="form-group">
                  <label class="control-label">
                    FINESS ÉTABLISSEMENT
                  </label>
                  <input disabled
                         class="form-control"
                         :value="surveillance.establishment.finess"
                         type="text"/>
                </div>
                <div class="form-group">
                  <label class="control-label">
                    UF/NOM DU SERVICE
                  </label>
                  <input disabled class="form-control"
                         :value="`${target.unit.unit.name} (${target.unit.code})`">
                </div>
                <sui-field-list v-model="target.model"
                                :fields="patientFields" :waiting="waiting">
                </sui-field-list>
                <collapse v-model="patientFormError.error">
                  <p>
                    <sui-error-panel :error="patientFormError"
                                   v-if="patientFormError.error">
                    </sui-error-panel>
                  </p>
                </collapse>
              </div>
            </modal>
            <alert type="warning">
              <h4>
                Bienvenue sur l'espace
                <span class="label label-reaExplaur">RÉA expl AUR</span>
                du programme SPIADI {{year}}
              </h4>
              <p>
                Vous trouverez ci-dessous la liste des services participant
                au module <span class="label label-reaExplaur">RÉA expl AUR</span>.
                Pour chacun de ces services, vous pourrez gérer le statut des
                écouvillons et remplir les fiches patients.
              </p>
            </alert>
            <alert type="warning">
              <h4>
                Procédure à suivre lors de l'identification d'une bactériémie à
                <i>S. aureus</i>
              </h4>
              <p>
                <ul class="process-list">
                  <li>
                    Sur cet espace, cliquer sur le bouton <strong>«Préparer un nouvel
                    écouvillon»</strong> dans le panneau du service dans lequel
                    la bactériémie a été identifiée
                  </li>
                  <li>
                    Une ligne «Écouvillon XXXXXXXXX-XX» apparait dans le panneau du
                    service, le numéro XXXXXXXXX-XX est <u><strong>l'identifiant</strong></u>
                    de cet écouvillon
                  </li>
                  <li>
                    <strong>
                      Reporter cet identifiant dans sa totalité (XXXXXXXXX-XX) sur
                      l'écouvillon à ensemencer
                    </strong>
                  </li>
                  <li>
                    <strong>
                      Reporter cet identifiant dans sa totalité (XXXXXXXXX-XX) sur
                      la fiche correspondant à la bactériémie, au niveau des
                      résistances à <i>S. aureus</i> (Champ «N° RÉA expl AUR»)
                    </strong>
                  </li>
                  <li>
                    Sur cet espace, cliquer sur le bouton <strong>«Remplir la fiche
                    patient»</strong> de l'écouvillon correspondant, puis remplir
                    le formulaire
                  </li>
                  <li>
                    <strong>
                      Merci de bien remplir la fiche patient avant
                      l'envoi de l'écouvillon pour éviter au personnel du
                      laboratoire de se retrouver bloqué lors de la réception
                    </strong>
                  </li>
                  <li>
                    <strong>
                      Envoyer sans délai l'écouvillon ensemencé à l'adresse suivante
                    </strong>
                    <p class="text-info text-center">
                      <strong>
                        Mme Sandra DOS SANTOS<br>
                        CRENO<br>
                        Laboratoire de Bactériologie et Hygiène du CHU Trousseau<br>
                        37044 Tours Cedex 9
                      </strong>
                    </p>
                  </li>
                  <li>
                    Notifier l'envoi de l'écouvillon à partir de cet espace
                    en cliquant sur le bouton <strong>«Envoyer Écouvillon»</strong>
                    correspondant
                  </li>
                </ul>
              </p>
            </alert>
            <div class="row">
              <div v-for="u in services"
                   :key="`panel-${u.unit.name}-${u.unit.ufType}`"
                   class="col-xs-12 col-md-6">
                <div class="panel panel-reaExplaur">
                  <div class="panel-heading">
                    <div class="panel-title">
                      {{u.unit.name}} ({{u.code}})
                    </div>
                  </div>
                  <div class="panel-body">
                    <alert type="warning" v-if="u.swabs.length === 0">
                      <p>
                        En attente de la préparation du premier écouvillon...
                      </p>
                    </alert>
                    <div v-if="u.swabs.length > 0">
                      <div v-for="(s, k) in u.swabs" :key="`swab-line-${k}`"
                           class="swab">
                        <h4>
                          Écouvillon {{ u.label }}{{ k + 1 }}
                        </h4>
                        <alert v-if="s.stat.val === 0" type="warning">
                          <h5>En attente de prélèvement...</h5>
                          <a class="btn btn-reaExplaur btn-sm"
                             :disabled="waiting"
                             @click="showPatientForm(u, k)">
                            <strong>Remplir la fiche Patient</strong>
                          </a>
                        </alert>
                        <alert v-else-if="s.stat.val === 1" type="warning">
                          <h5>
                            En attente d'envoi de l'écouvillon au laboratoire...
                          </h5>
                          <a class="btn btn-reaExplaur btn-sm"
                             :disabled="waiting"
                             @click="confirmNotifySent(u, k)">
                            <strong>Envoyer écouvillon</strong>
                          </a>
                        </alert>
                        <alert v-else-if="s.stat.val === 2" type="info">
                          <h5>
                            Écouvillon en cours d'acheminement vers le
                            laboratoire...
                          </h5>
                        </alert>
                        <alert v-else type="success">
                          <h5>Écouvillon reçu au laboratoire !</h5>
                        </alert>
                      </div>
                    </div>
                  </div>
                  <div class="panel-footer">
                    <a class="btn btn-reaExplaur btn-block"
                       :disabled="waiting"
                       @click="confirmNewSwab(u.unit.ufType, u.unit.name, u.code)">
                      Préparer un écouvillon
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import modules from '@/sui/data/modules'
import suiErrorPanel from '@/sui/components/errorpanel'
import Error from '@/sui/tools/error'
import suiFieldList from '@/sui/components/fieldlist'
import units from '@/sui/data/units'
import sc from '@/sui/sc/explaur'
import forms from '@/sui/tools/forms'
import { confirm } from '@/sui/tools/modal'

export default {
  name: 'rea-explAUR-dashboard',

  props: {
    surveillance: {
      type: Object
    },

    year: {
      type: String,
      required: true
    },

    module: {
      type: String,
      required: true
    }
  },

  data: function () {
    const patientFields = sc.patientFields(this.year)
    return {
      waiting: false,
      fields: sc.fields,
      patientFields: patientFields,
      formError: new Error(),
      patientFormError: new Error(),
      model: forms.newModel(sc.fields),
      unitFields: sc.unitFields(this.year),
      patientModal: false,
      target: {
        swab: -1,
        unit: null,
        model: forms.newModel(patientFields)
      }
    }
  },

  watch: {
    patientModal: function (v) {
      if (!v) {
        this.target.unit = null
        this.target.swab = -1
      }
    }
  },

  computed: {
    moduleName: function () {
      return modules[this.year][this.module].label
    },

    moduleData: function () {
      return this.surveillance.modules[this.module]
    },

    units: function () {
      const list = this.surveillance.getUfs(this.module)
      const result = []
      list.forEach(
        l => {
          result.push(
            {
              code: units[this.year].findFromIndex(l.ufType).code,
              unit: l,
              model: forms.newModel(this.unitFields)
            }
          )
        }
      )
      return result
    },

    services: function () {
      const list = this.surveillance.modules.reaExplaur.srvs
      const result = []
      list.forEach(
        l => {
          result.push(
            {
              code: units[this.year].findFromIndex(l.uf.ufType).code,
              unit: l.uf,
              label: `${this.surveillance.establishment.finess}-${l.labl}`,
              status: l.stat,
              swabs: l.epds
            }
          )
        }
      )
      return result
    },

    status: function () {
      return this.moduleData.stat.val
    },

    validForm: function () {
      for (let p in this.model) {
        const v = this.model[p]
        if (v.value === '' || v.error !== '') {
          return false
        }
      }

      for (let i in this.units) {
        const u = this.units[i]
        for (let p in u.model) {
          const v = u.model[p]
          const f = this.unitFields.find(i => i.key === p)
          const s = f.show
          if ((s == null || s(u.model)) &&
              (v.error !== '' || (!f.allowEmpty && v.value === ''))) {
            return false
          }
        }
      }

      return true
    },

    patientValidForm: function () {
      const m = this.target.model
      for (let p in m) {
        const v = m[p]
        const f = this.patientFields.find(i => i.key === p)
        const s = f.show
        if ((s == null || s(m)) &&
            (v.error !== '' || (!f.allowEmpty && v.value === ''))) {
          return false
        }
      }
      return true
    }
  },

  methods: {
    confirmNotifySent: function (u, k) {
      const swab = `${u.label}${k + 1}`
      const name = u.unit.name
      const code = u.code
      confirm(
        this,
        `<h3>Notification d'envoi d'un écouvillon</h3>
<p class="text-justify">
  Êtes-vous sûr.e de vouloir notifier l'envoi de l'écouvillon ${swab} issu
  du service ${name} (${code}) ?
</p>`
      ).then(() => {
        this.$nextTick(() => { this.notifySwabSent(u, k) })
      }).catch((e) => {})
    },

    notifySwabSent: function (u, k) {
      const vm = this
      const request = {
        id: this.surveillance.id,
        swab: k,
        uf: u.unit,
        stat: 2
      }
      vm.waiting = true
      vm.$store.dispatch('surveillances/setExplaurSwabStatus', request).then(
        r => {
          vm.waiting = false
          if (r.data.err != null) {
            vm.$notify(
              {
                type: 'danger',
                title: 'Erreur !',
                duration: 0,
                content: `Une erreur s'est produite pendant la notification
d'envoi d'un écouvillon. Si le problème persiste, merci de nous contacter via le
formulaire de contact.`
              }
            )
          } else {
            vm.$notify(
              {
                type: 'success',
                title: `Merci pour cet envoi, nous attendons votre écouvillon au laboratoire !`,
                duration: 0
              }
            )
          }
        }
      ).catch(
        e => {
          vm.waiting = false
          vm.$notify(
            {
              type: 'danger',
              title: 'Erreur !',
              duration: 0,
              content: `Une exception s'est produite pendant la notification
d'envoi d'un écouvillon. Si le problème persiste, merci de nous contacter via le
formulaire de contact.`
            }
          )
        }
      )
    },

    showPatientForm: function (u, k) {
      this.target.swab = k
      this.target.unit = u
      forms.resetModel(this.target.model, this.patientFields)
      this.patientModal = true
    },

    confirmNewSwab: function (ut, name, code) {
      confirm(
        this,
        `<h3>Préparation d'un nouvel écouvillon</h3>
<p class="text-justify">
  Êtes vous sûr.e de vouloir préparer un nouvel écouvillon pour le service
  ${name} (${code}) ?
</p>`
      ).then(() => {
        this.$nextTick(() => { this.newSwab(ut, name) })
      }).catch((e) => {})
    },

    newSwab: function (ut, name) {
      const vm = this
      const request = {
        id: this.surveillance.id,
        ut: ut,
        uf: name
      }
      vm.waiting = true
      vm.$store.dispatch('surveillances/newExplaurSwab', request).then(
        r => {
          vm.waiting = false
          if (r.data.err != null) {
            vm.$notify(
              {
                type: 'danger',
                title: 'Erreur !',
                duration: 0,
                content: `Une erreur s'est produite pendant la préparation
d'un nouvel écouvillon. Si le problème persiste, merci de nous contacter via le
formulaire de contact.`
              }
            )
          } else {
            vm.$notify(
              {
                type: 'success',
                title: 'Écouvillon ajouté !',
                duration: 0
              }
            )
          }
        }
      ).catch(
        e => {
          vm.waiting = false
          vm.$notify(
            {
              type: 'danger',
              title: 'Erreur !',
              duration: 0,
              content: `Une exception s'est produite pendant la préparation
d'un nouvel écouvillon. Si le problème persiste, merci de nous contacter via le
formulaire de contact.`
            }
          )
        }
      )
    },

    confirm: function (action, status) {
      confirm(
        this,
        `<h3>Changement de statut
<span class='label-bigger label label-reaExplAUR'>RÉA expl AUR</span></h3>
<p class="text-justify">
Êtes vous sûr.e de vouloir changer le statut de l'établissement
<strong>${this.surveillance.establishment.name}</strong> en
<strong>«${action}»</strong> ?</p>`
      ).then(() => {
        this.$nextTick(
          () => {
            this.setStatus(status)
          }
        )
      }).catch((e) => {})
    },

    setStatus: function (status) {
      const request = {
        id: this.surveillance.id,
        stat: status
      }
      const vm = this
      vm.waiting = true
      this.$store.dispatch('surveillances/updateReaExplaurStatus', request).then(
        r => {
          vm.waiting = false
          if (r.data.err != null) {
            vm.$notify(
              {
                type: 'danger',
                title: 'Erreur !',
                duration: 0,
                content: `Une erreur s'est produite pendant la mise à jour du
statut RÉA expl AUR. Si le problème persiste, merci de nous contacter via le
 formulaire de contact.`
              }
            )
          } else {
            vm.$notify(
              {
                type: 'success',
                title: 'Succès !',
                content: `Le changement de statut RÉA expl AUR a bien été effectué.`
              }
            )
          }
        }
      ).catch(
        e => {
          vm.waiting = false
          vm.$notify(
            {
              type: 'danger',
              title: 'Erreur !',
              duration: 0,
              content: `Une exception s'est produite pendant la mise à jour du
statut RÉA expl AUR. Si le problème persiste, merci de nous contacter via le
 formulaire de contact.`
            }
          )
        }
      )
    },

    notifyReceived: function () {
      this.confirm(`écouvillons reçus`, 3)
    },

    sendPatient: function () {
      if (!this.patientValidForm) {
        return
      }

      this.patientFormError.reset()
      const prms = {}

      const model = this.target.model
      this.patientFields.forEach(
        f => {
          if (f.show == null || f.show(model)) {
            const v = model[f.key].value
            prms[f.key] = f.convert == null ? v : f.convert(v)
          }
        }
      )
      const request = {
        id: this.surveillance.id,
        uf: this.target.unit.unit,
        swab: this.target.swab,
        prms: prms
      }
      this.waiting = true
      const vm = this
      const swab = `${this.target.unit.label}${this.target.swab + 1}`
      vm.$store.dispatch('surveillances/setExplaurPatient', request).then(
        r => {
          this.waiting = false
          if (r.data.err != null) {
            vm.setPatientError()
          } else {
            this.$notify(
              {
                type: 'success',
                title: `La fiche patient pour l'écouvillon ${swab} a bien été
enregistrée.`,
                duration: 10000
              }
            )
            this.patientModal = false
          }
          return r
        }
      ).catch(
        e => {
          this.waiting = false
          vm.setPatientError()
        }
      )
    },

    send: function () {
      if (!this.validForm) {
        return
      }

      this.formError.reset()
      const m = this.model
      const address = `${m.address.value} ${m.code.value} ${m.town.value}`
      const srvs = []
      this.units.forEach(
        u => {
          const prct = {}
          for (let p in this.unitFields) {
            const f = this.unitFields[p]
            if (f.show == null || f.show(u.model)) {
              const v = u.model[f.key].value
              prct[f.key] = f.convert == null ? v : f.convert(v)
            }
          }

          srvs.push(
            {
              uf: u.unit,
              prct: prct
            }
          )
        }
      )

      const request = {
        id: this.surveillance.id,
        mod: {
          dest: {
            firstName: m.firstName.value,
            name: m.name.value,
            role: m.job.value,
            service: m.unit.value,
            establishment: m.establishment.value,
            address: address
          },
          srvs: srvs
        }
      }
      this.waiting = true
      const vm = this
      vm.$store.dispatch('surveillances/startExplaur', request).then(
        r => {
          if (r.data.err != null) {
            this.setError()
          } else {
            this.$notify(
              {
                type: 'success',
                title: `Le module RÉA expl AUR a bien été mis à jour avec
le destinataire des écouvillons et la.es fiche.s service.s !`,
                duration: 10000,
                content: `Les écouvillons seront expédiés au plus tôt par notre
équipe.`
              }
            )
          }
          return r
        }
      ).catch(
        e => {
          vm.setError()
        }
      )
    },

    setPatientError: function () {
      this.patientFormError.set(
        'Erreur !',
        `Une erreur s'est produite lors de l'envoi du formulaire.`,
        `Merci de vérifier votre connection internet avant de réessayer.`,
        true
      )
    },

    setError: function () {
      this.waiting = false
      this.formError.set(
        'Erreur interne !',
        `Une erreur s'est produite lors de l'envoi du formulaire.`,
        `Merci de vérifier votre connection internet avant de réessayer.`,
        true
      )
    }
  },

  components: {
    'sui-field-list': suiFieldList,
    'sui-error-panel': suiErrorPanel
  }
}
</script>
<style scoped lang="less">
.panel-address {
  font-size: 110%;
  margin: 2rem;
}

.alert:last-child {
  margin-bottom: 0rem;
}

h4 {
  margin-top: 0rem;
}

.alert-success h3 {
  color: white;
}

.end-button {
  margin-bottom: 3rem;
  .btn {
    font-size: 140%;
  }
}

.swab {
  margin-bottom: 1.9rem;
  .alert {
    padding: 0.5rem;
  }

  h5 {
    margin-top: 0rem;
    font-weight: bold;
  }

  h5:last-child {
    margin-bottom: 0rem;
  }
}

.swab:last-child {
  margin-bottom: 0rem;
}

.process-list {
  list-style-type: decimal;
  li {
    margin-bottom: 0.5rem;
  }
}
</style>
